import { useQuery } from '@tanstack/react-query';

import { useZepApiClient } from '@zep/apis';
import { useAuth } from '@zep/auth';

export const AI_MAP_ELIGIBILITY_QUERY_KEY = 'ai-map-eligibility';
export const AI_MAP_ELIGIBILITY_PATH = '/v2/ai-map/eligibility';

type AiMapEligibilityResponse = { status: number; eligible?: boolean };

// Entry-point hint for the school map contest gate; the server's Authorize
// gate is the authority. Fail-closed: only a confirmed `true` is eligible —
// loading, an error and `false` all read as not eligible, which also keeps
// the button from flashing in and then disappearing.
export const useAiMapEligibility = () => {
  const client = useZepApiClient();
  const { user } = useAuth();
  const hashId = user?.hashId ?? null;

  const query = useQuery<boolean>(
    [AI_MAP_ELIGIBILITY_QUERY_KEY, hashId],
    async () => {
      const { data } = await client.get<AiMapEligibilityResponse>(
        AI_MAP_ELIGIBILITY_PATH,
      );
      return data?.eligible === true;
    },
    {
      enabled: hashId !== null,
      retry: false,
      // Revocation must show up on the next visit, not after a long cache.
      staleTime: 30_000,
      refetchOnWindowFocus: true,
      suspense: false,
      useErrorBoundary: false,
    },
  );

  return {
    isEligible: query.data === true,
    // Whether the verdict is in (either way). Without a user there is no
    // verdict yet, so callers keep showing their loading state.
    isSettled: hashId !== null && (query.isSuccess || query.isError),
  };
};
